/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useEffect } from 'react';

// @mui material components
import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import { Card, Stack } from '@mui/material';

// Vision UI Dashboard React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiProgress from 'components/VuiProgress';

// Vision UI Dashboard React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import MiniStatisticsCard from 'examples/Cards/StatisticsCards/MiniStatisticsCard';

// Vision UI Dashboard React functions
import linearGradient from 'assets/theme/functions/linearGradient';

// Vision UI Dashboard React base styles
import colors from 'assets/theme/base/colors';

// Dashboard layout components
import WelcomeMark from 'layouts/pages/rtl/components/WelcomeMark';
import Projects from 'layouts/pages/rtl/components/Projects';
import OrderOverview from 'layouts/pages/rtl/components/OrderOverview';
import SatisfactionRate from 'layouts/pages/rtl/components/SatisfactionRate';
import ReferralTracking from 'layouts/pages/rtl/components/ReferralTracking';

// React icons
import { IoIosRocket } from 'react-icons/io';
import { IoGlobe } from 'react-icons/io5';
import { IoBuild } from 'react-icons/io5';
import { IoWallet } from 'react-icons/io5';
import { IoDocumentText } from 'react-icons/io5';
import { FaShoppingCart } from 'react-icons/fa';

// Data
import BasicLineChart from 'examples/Charts/LineCharts/BasicLineChart';
import BarChart from 'examples/Charts/BarCharts/BasicBarChart';
import { lineChartDataDashboard } from 'layouts/pages/rtl/data/lineChartData';
import { lineChartOptionsDashboard } from 'layouts/pages/rtl/data/lineChartOptions';
import { barChartDataDashboard } from 'layouts/pages/rtl/data/barChartData';
import { barChartOptionsDashboard } from 'layouts/pages/rtl/data/barChartOptions';

// Vision UI Dashboard React contexts
import { useVisionUIController, setDirection } from 'context';

function RTL() {
  const { gradients } = colors;
  const { cardContent } = gradients;
  const [, dispatch] = useVisionUIController();

  // Changing the direction to rtl
  useEffect(() => {
    setDirection(dispatch, 'rtl');
    return () => setDirection(dispatch, 'ltr');
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: 'أموال اليوم', fontWeight: 'regular' }}
                count="$53,000"
                percentage={{ color: 'success', text: '+55%' }}
                icon={<IoWallet size="22px" color="white" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: 'مستخدمي اليوم' }}
                count="2,300"
                percentage={{ color: 'success', text: '+3%' }}
                icon={<IoGlobe size="22px" color="white" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: 'عملاء جدد' }}
                count="+3,462"
                percentage={{ color: 'error', text: '-2%' }}
                icon={<IoDocumentText size="22px" color="white" />}
              />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: 'إجمالي المبيعات' }}
                count="$103,430"
                percentage={{ color: 'success', text: '+5%' }}
                icon={<FaShoppingCart size="20px" color="white" />}
              />
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing="18px">
            <Grid item xs={12} xl={5}>
              <WelcomeMark />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
              <SatisfactionRate />
            </Grid>
            <Grid item xs={12} lg={6} xl={4}>
              <ReferralTracking />
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card>
                <VuiBox sx={{ height: '100%' }}>
                  <VuiTypography
                    variant="lg"
                    color="white"
                    fontWeight="bold"
                    mb="5px"
                  >
                    نظرة عامة على المبيعات
                  </VuiTypography>
                  <VuiBox display="flex" alignItems="center" mb="40px">
                    <VuiTypography
                      variant="button"
                      color="success"
                      fontWeight="bold"
                    >
                      + 5٪ أكثر
                      <VuiTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        في عام 2021
                      </VuiTypography>
                    </VuiTypography>
                  </VuiBox>
                  <VuiBox sx={{ height: '310px' }}>
                    <BasicLineChart
                      chartData={lineChartDataDashboard}
                      chartOptions={lineChartOptionsDashboard}
                    />
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>
            <Grid item xs={12} lg={5}>
              <Card>
                <VuiBox>
                  <VuiBox
                    mb="24px"
                    height="220px"
                    sx={{
                      background: linearGradient(
                        cardContent.main,
                        cardContent.state,
                        cardContent.deg,
                      ),
                      borderRadius: '20px',
                    }}
                  >
                    <BarChart
                      chartData={barChartDataDashboard}
                      chartOptions={barChartOptionsDashboard}
                    />
                  </VuiBox>
                  <VuiTypography
                    variant="lg"
                    color="white"
                    fontWeight="bold"
                    mb="5px"
                  >
                    المستخدمين النشطين
                  </VuiTypography>
                  <VuiBox display="flex" alignItems="center" mb="40px">
                    <VuiTypography
                      variant="button"
                      color="success"
                      fontWeight="bold"
                    >
                      (+23)
                      <VuiTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        من الأسبوع الماضي
                      </VuiTypography>
                    </VuiTypography>
                  </VuiBox>
                  <Grid container spacing="50px">
                    <Grid item xs={6} md={3} lg={3}>
                      <Stack
                        direction="row"
                        spacing={{ sm: '10px', xl: '4px', xxl: '10px' }}
                        mb="6px"
                      >
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            borderRadius: '6px',
                            width: '25px',
                            height: '25px',
                          }}
                        >
                          <IoWallet color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography
                          color="text"
                          variant="button"
                          fontWeight="medium"
                        >
                          المستخدمون
                        </VuiTypography>
                      </Stack>
                      <VuiTypography
                        color="white"
                        variant="lg"
                        fontWeight="bold"
                        mb="8px"
                      >
                        32,984
                      </VuiTypography>
                      <VuiProgress
                        value={60}
                        color="info"
                        sx={{ background: '#2D2E5F' }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3} lg={3}>
                      <Stack
                        direction="row"
                        spacing={{ sm: '10px', xl: '4px', xxl: '10px' }}
                        mb="6px"
                      >
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            borderRadius: '6px',
                            width: '25px',
                            height: '25px',
                          }}
                        >
                          <IoIosRocket color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography
                          color="text"
                          variant="button"
                          fontWeight="medium"
                        >
                          نقرات
                        </VuiTypography>
                      </Stack>
                      <VuiTypography
                        color="white"
                        variant="lg"
                        fontWeight="bold"
                        mb="8px"
                      >
                        2,42M
                      </VuiTypography>
                      <VuiProgress
                        value={60}
                        color="info"
                        sx={{ background: '#2D2E5F' }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3} lg={3}>
                      <Stack
                        direction="row"
                        spacing={{ sm: '10px', xl: '4px', xxl: '10px' }}
                        mb="6px"
                      >
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            borderRadius: '6px',
                            width: '25px',
                            height: '25px',
                          }}
                        >
                          <FaShoppingCart color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography
                          color="text"
                          variant="button"
                          fontWeight="medium"
                        >
                          مبيعات
                        </VuiTypography>
                      </Stack>
                      <VuiTypography
                        color="white"
                        variant="lg"
                        fontWeight="bold"
                        mb="8px"
                      >
                        2,400$
                      </VuiTypography>
                      <VuiProgress
                        value={60}
                        color="info"
                        sx={{ background: '#2D2E5F' }}
                      />
                    </Grid>
                    <Grid item xs={6} md={3} lg={3}>
                      <Stack
                        direction="row"
                        spacing={{ sm: '10px', xl: '4px', xxl: '10px' }}
                        mb="6px"
                      >
                        <VuiBox
                          bgColor="info"
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                          sx={{
                            borderRadius: '6px',
                            width: '25px',
                            height: '25px',
                          }}
                        >
                          <IoBuild color="#fff" size="12px" />
                        </VuiBox>
                        <VuiTypography
                          color="text"
                          variant="button"
                          fontWeight="medium"
                        >
                          العناصر
                        </VuiTypography>
                      </Stack>
                      <VuiTypography
                        color="white"
                        variant="lg"
                        fontWeight="bold"
                        mb="8px"
                      >
                        320
                      </VuiTypography>
                      <VuiProgress
                        value={60}
                        color="info"
                        sx={{ background: '#2D2E5F' }}
                      />
                    </Grid>
                  </Grid>
                </VuiBox>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={8}>
            <Projects />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <OrderOverview />
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RTL;
